import type { IUserSearchItem } from "@frontend-shared/types";
import { ChevronDown, Loader2 } from "lucide-react";
import { memo, useMemo, useRef } from "react";

import { Button } from "@/components/ui/button";
import { Command, CommandGroup, CommandList } from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { SUGGESTIONS_GROUP_CLASS_NAME } from "./constants";
import { ShareExtraOptionItem } from "./ShareExtraOptionItem";
import { ShareInviteSelectionFooter } from "./ShareInviteSelectionFooter";
import { ShareSelectedChip } from "./ShareSelectedChip";
import { ShareSuggestionsPanel } from "./ShareSuggestionsPanel";
import { getUserSuggestionName } from "./suggestion-utils";
import type { IShareInviteInputProps } from "./types";
import { useKeepQueryInView } from "./use-keep-query-in-view";
import { UserSuggestionItem } from "./UserSuggestionItem";

const ShareInviteInputComponent = ({
  selectedUsers,
  searchQuery,
  onSearchQueryChange,
  onToggleUser,
  onRemoveUser,
  onClearSelectedUsers,
  onInvite,
  onKeyDown,
  onOpenDropdown,
  onToggleDropdown,
  isSearching,
  isSharing,
  canInviteCurrent,
  isDropdownVisible,
  hasResults,
  filteredProjectMembers,
  filteredOrgMembers,
  highlightedValue,
  inputRef,
  suggestionListRef,
  containerRef,
  submitDisabled = false,
  selectionLimit,
  inviteAdornment,
  extraGroup,
  suggestionsPortal,
  labels,
}: IShareInviteInputProps): JSX.Element => {
  const fieldRef = useRef<HTMLDivElement>(null);
  const selectedExtraOptions = useMemo(
    () => extraGroup?.selectedOptions ?? [],
    [extraGroup?.selectedOptions],
  );
  const extraOptions = useMemo(
    () => extraGroup?.options ?? [],
    [extraGroup?.options],
  );
  // NOTE: NSAI-6501 — the pending batch is people AND extra options, so the
  // count, the "clear all" row and the Invite button all read both.
  const selectedCount = selectedUsers.length + selectedExtraOptions.length;
  const chipRowRef = useKeepQueryInView({ selectedCount });
  const hasSelectedUsers = selectedCount > 0;
  const isInputDisabled = !canInviteCurrent;
  const shouldShowSuggestions = isDropdownVisible;
  // NOTE: At the cap the picker keeps working — the query, the chips and the
  // ticked rows all stay usable — it just refuses to grow the selection, so the
  // user swaps someone out instead of hitting a server-side rejection.
  const isSelectionLimitReached =
    selectionLimit !== undefined && selectedUsers.length >= selectionLimit.max;

  const selectedUserUuids = useMemo(
    () => new Set(selectedUsers.map((selectedUser) => selectedUser.uuid)),
    [selectedUsers],
  );

  const selectedExtraIds = useMemo(
    () => new Set(selectedExtraOptions.map((option) => option.id)),
    [selectedExtraOptions],
  );

  const renderMemberOption = (user: IUserSearchItem): JSX.Element => {
    const name = getUserSuggestionName({
      user,
      fallback: labels.unknownUser,
    });

    return (
      <UserSuggestionItem
        key={user.uuid}
        user={user}
        isSelected={selectedUserUuids.has(user.uuid)}
        isDisabled={
          isSelectionLimitReached && !selectedUserUuids.has(user.uuid)
        }
        onToggle={onToggleUser}
        unknownUserLabel={labels.unknownUser}
        selectAriaLabel={labels.formatSelectMemberAria({ name })}
        unselectAriaLabel={labels.formatUnselectMemberAria({ name })}
      />
    );
  };

  const selectionFooter = hasSelectedUsers ? (
    <ShareInviteSelectionFooter
      selectedCountLabel={labels.formatSelectedCount({
        current: selectedCount,
      })}
      clearAllLabel={labels.clearAll}
      onClearAll={onClearSelectedUsers}
    />
  ) : null;

  return (
    // NOTE: NSAI-7350 — `items-start`, not `items-end`: the Invite button lines
    // up with the field's first row, so a field that grows with chips extends
    // downwards without dragging the button along. The field starts from a zero
    // basis and takes whatever the submit button leaves it, so the two never
    // wrap apart — the members list opens under the field and must not cover
    // the button, whatever the button's label length (Russian "Поделиться" is
    // 116px against "Share"'s 71px). Below `sm` an adornment (the skill
    // dialog's role select) moves to a line of its own ABOVE the field: that
    // keeps the field from being squeezed beside it on a phone, and keeps it
    // clear of the members list, which opens under the field.
    <div className="flex flex-wrap items-start gap-2 py-0">
      {/* NOTE: The outside-click guard is anchored here, so the panel and its
      selection footer count as part of the control — clearing a selection must
      not collapse the members list the user is still working through. */}
      <div ref={containerRef} className="min-w-0 flex-1 basis-0">
        <div ref={fieldRef} className="relative">
          {/* NOTE: NSAI-7350 — two columns: the chips and the query wrap inside
        the first, the spinner and the chevron sit in the second. The chevron
        used to be one more wrapping item, so the first chip pushed it onto a
        row of its own. The focus treatment is the one-pixel ring the dialog's
        other fields read as — not a 2px ring with an offset gap. */}
          <div
            className={cn(
              "flex min-h-9 w-full items-center gap-2 rounded-md border border-input bg-background ps-3 pe-1.5 text-sm transition-[border-color,box-shadow]",
              "focus-within:border-ring focus-within:ring-1 focus-within:ring-ring",
              isInputDisabled && "cursor-not-allowed opacity-50",
            )}
          >
            <div
              ref={chipRowRef}
              className="themed-scrollbar flex max-h-20 min-w-0 flex-1 flex-wrap items-center gap-1 overflow-y-auto overscroll-contain py-1"
            >
              {selectedExtraOptions.map((option) => (
                <ShareSelectedChip
                  key={option.id}
                  label={option.label}
                  removeAriaLabel={
                    extraGroup?.formatUnselectAria({ name: option.label }) ??
                    option.label
                  }
                  onRemove={() => extraGroup?.onRemove(option.id)}
                  testId="share-selected-extra-chip"
                />
              ))}

              {selectedUsers.map((selectedUser) => {
                const name = getUserSuggestionName({
                  user: selectedUser,
                  fallback: labels.unknownUser,
                });

                return (
                  <ShareSelectedChip
                    key={selectedUser.uuid}
                    label={name}
                    removeAriaLabel={labels.formatUnselectMemberAria({ name })}
                    onRemove={() => onRemoveUser(selectedUser.uuid)}
                    testId="share-selected-chip"
                  />
                );
              })}

              <Input
                ref={inputRef}
                value={searchQuery}
                // NOTE: Typing and clicking the field are explicit requests for
                // the members list, so both reveal it. Focus alone does not —
                // otherwise the dialog's autofocus would expand the list before
                // the user asked for it (NSAI-5025).
                onChange={(event) => {
                  onSearchQueryChange(event.target.value);
                  onOpenDropdown();
                }}
                onClick={onOpenDropdown}
                onKeyDown={onKeyDown}
                placeholder={labels.placeholder}
                aria-label={labels.inputAriaLabel}
                className="h-6 min-h-0 w-auto min-w-[6rem] flex-1 rounded-none border-0 bg-transparent p-0 shadow-none outline-none placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-100"
                disabled={isInputDisabled}
              />
            </div>

            <div className="flex shrink-0 items-center gap-1 self-center">
              {isSearching && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}

              <Button
                type="button"
                variant="ghost"
                onClick={onToggleDropdown}
                disabled={isInputDisabled}
                aria-expanded={isDropdownVisible}
                aria-label={
                  isDropdownVisible ? labels.hideMembers : labels.showMembers
                }
                data-testid="share-members-toggle"
                className="h-6 w-6 rounded-sm p-0 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-100"
              >
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform",
                    isDropdownVisible && "rotate-180",
                  )}
                />
              </Button>
            </div>
          </div>

          {shouldShowSuggestions &&
            (suggestionsPortal !== undefined || !isSearching || hasResults) && (
              <ShareSuggestionsPanel
                anchorRef={fieldRef}
                containerRef={containerRef}
                portal={suggestionsPortal}
                label={labels.showMembers}
                testId={hasResults ? "share-members-dropdown" : undefined}
              >
                {!hasResults && !isSearching && (
                  <p className="px-2 py-4 text-center text-sm text-muted-foreground">
                    {labels.noResults}
                  </p>
                )}
                {!hasResults && isSearching && (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                )}
                {hasResults && (
                  <div
                    ref={suggestionListRef}
                    className="themed-scrollbar min-h-0 max-h-[min(28rem,60vh)] overflow-y-auto overscroll-contain p-1"
                  >
                    <Command
                      shouldFilter={false}
                      value={highlightedValue}
                      className="h-auto overflow-visible rounded-none bg-transparent"
                    >
                      <CommandList className="max-h-none overflow-visible">
                        {extraGroup !== undefined &&
                          extraOptions.length > 0 && (
                            <CommandGroup
                              heading={extraGroup.heading}
                              className={SUGGESTIONS_GROUP_CLASS_NAME}
                            >
                              {extraOptions.map((option) => (
                                <ShareExtraOptionItem
                                  key={option.id}
                                  option={option}
                                  icon={extraGroup.icon}
                                  isSelected={selectedExtraIds.has(option.id)}
                                  onToggle={extraGroup.onToggle}
                                  selectAriaLabel={extraGroup.formatSelectAria({
                                    name: option.label,
                                  })}
                                  unselectAriaLabel={extraGroup.formatUnselectAria(
                                    {
                                      name: option.label,
                                    },
                                  )}
                                />
                              ))}
                            </CommandGroup>
                          )}

                        {filteredProjectMembers.length > 0 && (
                          <CommandGroup
                            heading={labels.projectMembersHeading}
                            className={SUGGESTIONS_GROUP_CLASS_NAME}
                          >
                            {filteredProjectMembers.map(renderMemberOption)}
                          </CommandGroup>
                        )}

                        {filteredOrgMembers.length > 0 && (
                          <CommandGroup
                            heading={labels.organizationMembersHeading}
                            className={SUGGESTIONS_GROUP_CLASS_NAME}
                          >
                            {filteredOrgMembers.map(renderMemberOption)}
                          </CommandGroup>
                        )}
                      </CommandList>
                    </Command>
                  </div>
                )}
                {selectionFooter}
              </ShareSuggestionsPanel>
            )}
        </div>

        {isSelectionLimitReached && (
          <p
            className="mt-1.5 text-xs text-muted-foreground"
            data-testid="share-selection-limit-notice"
          >
            {selectionLimit?.reachedMessage}
          </p>
        )}
      </div>

      {inviteAdornment !== undefined && (
        <div className="order-first w-full sm:order-none sm:w-auto sm:shrink-0">
          {inviteAdornment}
        </div>
      )}

      <Button
        onClick={onInvite}
        disabled={
          !canInviteCurrent ||
          isSharing ||
          selectedCount === 0 ||
          submitDisabled
        }
        className="h-9 shrink-0 rounded-md"
      >
        {labels.invite}
      </Button>
    </div>
  );
};

export const ShareInviteInput = memo(ShareInviteInputComponent);

ShareInviteInput.displayName = "ShareInviteInput";
